import React, { useMemo, useRef, useState } from 'react';
import { MiniMap, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { useIsMobile, useIsSmallPhone } from '@/hooks/useIsMobile';
import { WorkbenchDrawer } from './WorkbenchDrawer';
import { WorkbenchToolbar } from './WorkbenchToolbar';
import { SmallPhoneLoomNotice } from './SmallPhoneLoomNotice';
import { TabletLoomTip } from './TabletLoomTip';

const CANVAS_WIDTH = 1440;
const CANVAS_HEIGHT = 900;

const initialScale = () => {
  if (typeof window === 'undefined') return 0.5;
  return Math.min(0.8, Math.max(0.45, window.innerWidth / 900));
};

export const MobileWorkbenchShell = ({ children, surface = 'loom' }) => {
  const mobile = useIsMobile();
  const smallPhone = useIsSmallPhone();
  const transformRef = useRef(null);
  const startScale = useMemo(initialScale, []);
  const [scale, setScale] = useState(startScale);
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (!mobile) return children;
  // iter-admin-mobile-native — the pan/zoom desktop-canvas treatment
  // below is built for the spatial Loom editor surface, which doesn't
  // reflow to a narrow screen. The admin console (and any other
  // non-loom surface) is plain scrollable content with its own
  // responsive header/drawer already built for mobile — wrapping it
  // in a 1440px zoomed-out canvas just shrank every control (reported
  // bug: the panel drawer button was unreachable, rendered at ~45%
  // scale). Render those surfaces natively instead.
  if (surface !== 'loom') return children;

  const setView = (nextScale) => {
    transformRef.current?.setTransform(0, 0, nextScale, 220, 'easeOut');
  };
  const changeZoom = (delta) => {
    const current = transformRef.current?.state;
    const nextScale = Math.max(0.2, Math.min(2, (current?.scale || scale) + delta));
    transformRef.current?.setTransform(
      current?.positionX || 0,
      current?.positionY || 0,
      nextScale,
      180,
      'easeOut',
    );
  };
  const fitWidth = () => {
    const width = transformRef.current?.instance?.wrapperComponent?.clientWidth || window.innerWidth;
    setView(Math.max(0.2, Math.min(1, (width - 16) / CANVAS_WIDTH)));
  };

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-secondary" data-testid={`mobile-workbench-${surface}`}>
      {surface === 'loom' && smallPhone && <SmallPhoneLoomNotice />}
      {surface === 'loom' && !smallPhone && <TabletLoomTip />}
      <WorkbenchToolbar
        surface={surface}
        scale={scale}
        onMenu={() => setDrawerOpen(true)}
        onZoomIn={() => changeZoom(0.15)}
        onZoomOut={() => changeZoom(-0.15)}
        onFit={fitWidth}
        onActual={() => setView(1)}
        onReset={() => setView(startScale)}
      />
      <div className="relative min-h-0 flex-1 overflow-hidden texture-grid" data-testid="workbench-pan-viewport">
        <TransformWrapper
          ref={transformRef}
          initialScale={startScale}
          minScale={0.2}
          maxScale={2}
          centerOnInit={false}
          centerZoomedOut={false}
          limitToBounds
          wheel={{ step: 0.08 }}
          panning={{ excluded: ['button', 'a', 'input', 'textarea', 'select'] }}
          pinch={{ step: 4 }}
          doubleClick={{ mode: 'toggle', step: 0.35 }}
          onTransform={(_ref, state) => setScale(state.scale)}
        >
          <TransformComponent
            wrapperStyle={{ width: '100%', height: '100%' }}
            contentStyle={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}
          >
            <div
              className="h-full w-full overflow-auto bg-background text-foreground shadow-2xl"
              style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}
              data-testid="workbench-desktop-canvas"
            >
              {children}
            </div>
          </TransformComponent>
          <div className="pointer-events-none absolute bottom-3 right-3 z-20 overflow-hidden rounded-sm border border-border bg-card/95 p-1 shadow-lg" data-testid="workbench-minimap">
            <MiniMap
              width={112}
              height={70}
              panning={false}
              borderColor="hsl(var(--primary))"
              previewStyle={{ borderWidth: 2, boxShadow: 'rgba(0,0,0,.28) 0 0 0 9999px' }}
            >
              <div className="relative bg-background" style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}>
                <div className="absolute inset-y-0 left-0 w-64 bg-panel-header" />
                <div className="absolute left-72 right-10 top-12 h-16 bg-primary/20" />
                <div className="absolute bottom-12 left-72 right-10 top-36 grid grid-cols-3 gap-8">
                  <div className="bg-card" /><div className="bg-card" /><div className="bg-card" />
                </div>
              </div>
            </MiniMap>
          </div>
        </TransformWrapper>
      </div>
      <WorkbenchDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} surface={surface} />
    </div>
  );
};